import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ListChecks, GripVertical, CheckCircle2, Circle, X, Eye, EyeOff, CalendarClock, AlertCircle, CreditCard, Mail, Loader2, Check, AlertTriangle, RotateCw } from "lucide-react";

const COLS = [
  { id: "decouverte", label: "Découverte" },
  { id: "cadrage", label: "Cadrage" },
  { id: "design", label: "Design" },
  { id: "dev", label: "Développement" },
  { id: "livraison", label: "Livraison" },
  { id: "termine", label: "Terminé" },
];

const progressOf = (p) => {
  const t = p?.tasks || [];
  if (!t.length) return 0;
  return Math.round((t.filter((x) => x.done).length / t.length) * 100);
};

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // project being edited (full modal)
  const [dragId, setDragId] = useState(null);
  const [form, setForm] = useState({ title: "", client_name: "", description: "", type: "mvp", budget_eur: "", company_id: "" });

  const load = () => api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  const loadCompanies = () => api.get("/admin/entrepreneurs").then((r) => {
    // Flatten companies across entrepreneurs
    const all = [];
    (r.data || []).forEach((e) => (e.companies || []).forEach((c) => all.push({ id: c.id, name: c.name, entrepreneur: e.full_name || e.email })));
    setCompanies(all);
  }).catch(() => setCompanies([]));
  useEffect(() => { load(); loadCompanies(); }, []);

  const update = async (id, body) => {
    try { await api.patch(`/admin/projects/${id}`, body); load(); } catch { toast.error("Échec"); }
  };

  const create = async () => {
    if (!form.title || !form.client_name) { toast.error("Titre et client requis"); return; }
    const payload = { ...form, budget_eur: form.budget_eur ? parseFloat(form.budget_eur) : null, company_id: form.company_id || null };
    try { await api.post("/admin/projects", payload); setOpen(false); setForm({ title: "", client_name: "", description: "", type: "mvp", budget_eur: "", company_id: "" }); load(); toast.success("Projet créé"); } catch { toast.error("Échec création"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce projet ?")) return;
    try { await api.delete(`/admin/projects/${id}`); load(); } catch { toast.error("Échec"); }
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Projets — Pipeline</h1>
          <p className="text-sm text-[#64748B] mt-1">Gérez vos projets clients de la découverte à la livraison. Cliquez sur une carte pour piloter les tâches.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="project-add-btn" className="bg-[#0A1128] hover:bg-[#14213d] text-white"><Plus size={16} className="mr-2" /> Nouveau projet</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau projet</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Titre *</Label><Input data-testid="project-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Client *</Label><Input data-testid="project-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
              <div>
                <Label>Entreprise liée (espace client)</Label>
                <select data-testid="project-company-link" className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
                  <option value="">— Aucune (non visible côté client) —</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.entrepreneur})</option>)}
                </select>
              </div>
              <div><Label>Type</Label>
                <select className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="mvp">MVP</option><option value="crm">CRM</option><option value="erp">ERP</option><option value="dashboard">Dashboard</option><option value="autre">Autre</option>
                </select>
              </div>
              <div><Label>Budget (€)</Label><Input data-testid="project-budget" type="number" value={form.budget_eur} onChange={(e) => setForm({ ...form, budget_eur: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea data-testid="project-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button data-testid="project-save" onClick={create} className="bg-[#0A1128] hover:bg-[#14213d] text-white">Créer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <PendingDeliveryBanner projects={projects} onSelect={(p) => setEditing(p)} />

      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 overflow-x-auto">
        {COLS.map((col) => {
          const items = projects.filter((p) => p.status === col.id);
          return (
            <div key={col.id} className="kanban-col p-3" data-testid={`projects-col-${col.id}`} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) update(dragId, { status: col.id }); setDragId(null); }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold tracking-wider uppercase text-[#0A1128]">{col.label}</span>
                <span className="text-xs font-bold text-[#64748B]">{items.length}</span>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {items.map((p) => {
                  const pct = progressOf(p);
                  return (
                    <div key={p.id} draggable onDragStart={() => setDragId(p.id)} onDragEnd={() => setDragId(null)} onClick={() => setEditing(p)} className={`kanban-card cursor-pointer ${dragId === p.id ? "dragging" : ""}`} data-testid={`project-card-${p.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="display font-bold text-sm text-[#0A1128]">{p.title}</div>
                        <button onClick={(e) => { e.stopPropagation(); del(p.id); }} className="text-[#94a3b8] hover:text-red-500"><Trash2 size={14} /></button>
                      </div>
                      <div className="text-xs text-[#64748B]">{p.client_name}</div>
                      {(p.tasks || []).length > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-[10px] text-[#64748B] font-bold mb-1">
                            <span>{p.tasks.filter((t) => t.done).length}/{p.tasks.length} tâches</span>
                            <span className="text-[#06B6D4]">{pct}%</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-black/8 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-[#06B6D4] to-[#A7F3D0] transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-2 flex-wrap">
                        {p.budget_eur ? <span className="text-xs text-[#16a34a] font-bold">{p.budget_eur.toLocaleString("fr-FR")} €</span> : null}
                        {p.type && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#A7F3D0]/40 text-[#0A1128] font-semibold uppercase">{p.type}</span>}
                        {p.company_id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#06B6D4]/15 text-[#06B6D4] font-bold inline-flex items-center gap-1"><Eye size={10} /> client</span>}
                        {p.order_ref && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0A1128]/10 text-[#0A1128] font-bold">{p.order_ref}</span>}
                        {p.source_proposal_id && !p.delivery_date && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFDCA8] text-[#92400E] font-bold inline-flex items-center gap-1" title="Date de livraison à définir — déclenche le MRR" data-testid={`project-pending-delivery-${p.id}`}>
                            <AlertCircle size={10} /> Date à définir
                          </span>
                        )}
                        {p.delivery_date && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#A7F3D0]/60 text-[#065F46] font-bold inline-flex items-center gap-1" title="Date de livraison">
                            <CalendarClock size={10} /> {p.delivery_date}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {items.length === 0 && <div className="text-xs text-[#64748B] italic text-center py-4">Vide</div>}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <ProjectEditModal project={editing} companies={companies} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function PendingDeliveryBanner({ projects, onSelect }) {
  const pending = (projects || []).filter((p) => p.source_proposal_id && !p.delivery_date);
  if (pending.length === 0) return null;
  return (
    <div className="mt-4 mb-3 rounded-2xl border border-[#FFDCA8] bg-[#FFDCA8]/30 p-4" data-testid="pending-delivery-banner">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#FFDCA8] flex items-center justify-center text-[#92400E]">
          <CalendarClock size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="display font-extrabold text-[#0A1128] text-sm">
            {pending.length} projet{pending.length > 1 ? "s" : ""} en attente de date de livraison
          </div>
          <div className="text-xs text-[#64748B] mt-0.5">
            La date de livraison enclenche le démarrage des services récurrents (MRR) et alimente la projection de trésorerie.
          </div>
          <div className="flex flex-wrap gap-2 mt-2.5">
            {pending.slice(0, 5).map((p) => (
              <button
                key={p.id}
                onClick={() => onSelect(p)}
                data-testid={`pending-delivery-item-${p.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#FFDCA8] text-[11px] font-bold text-[#0A1128] hover:bg-[#FFDCA8]/40 transition-colors"
              >
                {p.order_ref || "—"} · {p.title}
              </button>
            ))}
            {pending.length > 5 && (
              <span className="inline-flex items-center px-3 py-1.5 text-[11px] text-[#92400E] font-bold">+ {pending.length - 5} autre(s)…</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectEditModal({ project, companies, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => ({
    title: project.title,
    client_name: project.client_name,
    description: project.description || "",
    type: project.type || "mvp",
    budget_eur: project.budget_eur || "",
    company_id: project.company_id || "",
    client_visible: project.client_visible !== false,
    delivery_date: project.delivery_date || "",
    tasks: (project.tasks || []).map((t) => ({ ...t })),
  }));
  const [newTask, setNewTask] = useState("");
  const [busy, setBusy] = useState(false);
  const initialDelivery = project.delivery_date || "";

  const addTask = () => {
    if (!newTask.trim()) return;
    const next = [...draft.tasks, { id: crypto.randomUUID(), label: newTask.trim(), done: false, position: draft.tasks.length }];
    setDraft({ ...draft, tasks: next });
    setNewTask("");
  };

  const toggleTask = (id) => {
    setDraft({ ...draft, tasks: draft.tasks.map((t) => t.id === id ? { ...t, done: !t.done } : t) });
  };
  const removeTask = (id) => setDraft({ ...draft, tasks: draft.tasks.filter((t) => t.id !== id) });
  const moveTask = (idx, dir) => {
    const arr = [...draft.tasks];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    arr.forEach((t, i) => (t.position = i));
    setDraft({ ...draft, tasks: arr });
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        title: draft.title,
        client_name: draft.client_name,
        description: draft.description,
        type: draft.type,
        budget_eur: draft.budget_eur ? parseFloat(draft.budget_eur) : null,
        company_id: draft.company_id || null,
        client_visible: draft.client_visible,
        tasks: draft.tasks.map((t, i) => ({ id: t.id, label: t.label, done: !!t.done, position: i })),
      };
      await api.patch(`/admin/projects/${project.id}`, body);
      // If delivery_date was set/changed, call dedicated endpoint to also recompute the schedule
      if (draft.delivery_date && draft.delivery_date !== initialDelivery) {
        await api.patch(`/admin/projects/${project.id}/delivery-date`, { delivery_date: draft.delivery_date });
        toast.success("Date de livraison enregistrée — services récurrents enclenchés à partir de cette date 🚀");
      } else {
        toast.success("Projet mis à jour");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const pct = draft.tasks.length ? Math.round((draft.tasks.filter((t) => t.done).length / draft.tasks.length) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="project-edit-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between">
          <div>
            <div className="display font-extrabold text-[#0A1128] text-lg">Pilotage du projet</div>
            <div className="text-xs text-[#64748B]">Tâches, progression, visibilité côté client</div>
          </div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0A1128]"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-5">
          {/* Order context — order_ref + delivery_date */}
          {(project.order_ref || project.source_proposal_id) && (
            <div className={`rounded-2xl p-4 border ${draft.delivery_date ? "bg-[#A7F3D0]/20 border-[#A7F3D0]" : "bg-[#FFDCA8]/30 border-[#FFDCA8]"}`} data-testid="proj-edit-delivery-block">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: draft.delivery_date ? "#A7F3D0" : "#FFDCA8", color: draft.delivery_date ? "#065F46" : "#92400E" }}>
                  <CalendarClock size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="display font-extrabold text-[#0A1128] text-sm flex items-center gap-2 flex-wrap">
                    Bon de commande {project.order_ref || "—"}
                    {project.proposal_ref && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white text-[#0A1128]">Devis {project.proposal_ref}</span>}
                  </div>
                  <div className="text-xs text-[#64748B] mt-0.5">
                    {draft.delivery_date
                      ? "Date de livraison définie — les services récurrents commencent à cette date."
                      : "Définissez la date de livraison pour enclencher les services récurrents (MRR) et la projection de trésorerie."}
                  </div>
                  <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#0A1128]">Date de livraison</Label>
                    <Input
                      type="date"
                      data-testid="proj-edit-delivery-date"
                      value={draft.delivery_date}
                      onChange={(e) => setDraft({ ...draft, delivery_date: e.target.value })}
                      className="max-w-[180px] h-8 text-xs"
                    />
                    {draft.delivery_date && initialDelivery && draft.delivery_date === initialDelivery && (
                      <span className="text-[10px] text-[#065F46] font-bold">✓ enregistrée</span>
                    )}
                    {draft.delivery_date && draft.delivery_date !== initialDelivery && (
                      <span className="text-[10px] text-[#92400E] font-bold">⚠ modification non enregistrée</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Titre</Label><Input data-testid="proj-edit-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
            <div><Label>Client</Label><Input data-testid="proj-edit-client" value={draft.client_name} onChange={(e) => setDraft({ ...draft, client_name: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <Label>Entreprise liée (espace client)</Label>
              <select data-testid="proj-edit-company" className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" value={draft.company_id} onChange={(e) => setDraft({ ...draft, company_id: e.target.value })}>
                <option value="">— Aucune —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.entrepreneur})</option>)}
              </select>
            </div>
            <div><Label>Type</Label>
              <select className="w-full border border-black/10 rounded-md px-3 py-2 text-sm" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })}>
                <option value="mvp">MVP</option><option value="crm">CRM</option><option value="erp">ERP</option><option value="dashboard">Dashboard</option><option value="autre">Autre</option>
              </select>
            </div>
            <div><Label>Budget (€)</Label><Input type="number" value={draft.budget_eur} onChange={(e) => setDraft({ ...draft, budget_eur: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input id="proj-client-visible" type="checkbox" checked={draft.client_visible} onChange={(e) => setDraft({ ...draft, client_visible: e.target.checked })} data-testid="proj-edit-visible" />
              <Label htmlFor="proj-client-visible" className="cursor-pointer inline-flex items-center gap-1.5">
                {draft.client_visible ? <Eye size={14} /> : <EyeOff size={14} />} Visible côté client (espace entreprise)
              </Label>
            </div>
          </div>

          <div className="border-t border-black/8 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <ListChecks size={16} className="text-[#06B6D4]" />
              <Label className="font-extrabold text-[#0A1128]">Tâches du projet</Label>
              <span className="ml-auto text-xs text-[#64748B]">{draft.tasks.filter((t) => t.done).length}/{draft.tasks.length} · <span className="text-[#06B6D4] font-bold">{pct}%</span></span>
            </div>
            <div className="h-2 rounded-full bg-black/8 overflow-hidden mb-3">
              <div className="h-full rounded-full bg-gradient-to-r from-[#06B6D4] to-[#A7F3D0] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>

            <div className="flex gap-2 mb-3">
              <Input
                data-testid="proj-task-new"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
                placeholder="Ajouter une tâche (ex: Validation maquettes)"
              />
              <Button onClick={addTask} className="bg-[#0A1128] text-white hover:bg-[#14213d]" data-testid="proj-task-add"><Plus size={14} /></Button>
            </div>

            {draft.tasks.length === 0 ? (
              <div className="text-sm text-[#64748B] italic text-center py-6 border border-dashed border-black/15 rounded-xl">Aucune tâche pour l'instant. Ajoutez-en pour activer la barre de progression côté client.</div>
            ) : (
              <ul className="space-y-1.5">
                {draft.tasks.map((t, idx) => (
                  <li key={t.id} data-testid={`proj-task-${t.id}`} className="flex items-center gap-2 bg-[#FAFAFA] rounded-lg p-2 border border-black/5">
                    <div className="flex flex-col">
                      <button onClick={() => moveTask(idx, -1)} className="text-[#94a3b8] hover:text-[#0A1128] disabled:opacity-30" disabled={idx === 0}><GripVertical size={12} /></button>
                    </div>
                    <button onClick={() => toggleTask(t.id)} className="text-[#06B6D4] shrink-0" data-testid={`proj-task-toggle-${t.id}`}>
                      {t.done ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                    </button>
                    <input
                      value={t.label}
                      onChange={(e) => setDraft({ ...draft, tasks: draft.tasks.map((x) => x.id === t.id ? { ...x, label: e.target.value } : x) })}
                      className={`flex-1 bg-transparent text-sm outline-none ${t.done ? "line-through text-[#64748B]" : "text-[#0A1128]"}`}
                    />
                    <button onClick={() => removeTask(t.id)} className="text-[#94a3b8] hover:text-red-500"><Trash2 size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Installments management (only for projects auto-created from a signed proposal) */}
          {project.source_proposal_id && (project.installments || []).length > 0 && (
            <InstallmentsPanel project={project} onChanged={onSaved} />
          )}
        </div>

        <div className="px-6 py-4 border-t border-black/8 flex justify-end gap-2 bg-[#FAFAFA]">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={busy} className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold" data-testid="proj-edit-save">
            {busy ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const STATUS_PILL = {
  pending: { label: "À échoir", bg: "bg-[#FAFAFA] border-black/10 text-[#64748B]" },
  invoiced: { label: "Facturée", bg: "bg-[#06B6D4]/15 border-[#06B6D4]/30 text-[#06B6D4]" },
  paid: { label: "Payée", bg: "bg-[#A7F3D0]/40 border-[#A7F3D0] text-[#065F46]" },
  failed: { label: "Échec", bg: "bg-red-100 border-red-300 text-red-700" },
  partial: { label: "Partielle", bg: "bg-[#FFDCA8]/40 border-[#FFDCA8] text-[#92400E]" },
};

function InstallmentsPanel({ project, onChanged }) {
  const [proj, setProj] = useState(project);
  const [busy, setBusy] = useState(null); // installment index currently being processed
  const [modeBusy, setModeBusy] = useState(false);
  const hasCard = !!(proj.stripe_customer_id && proj.stripe_payment_method_id);
  const mode = proj.payment_mode || "manual_link";

  const refresh = async () => {
    try {
      const r = await api.get("/admin/projects");
      const fresh = (r.data || []).find((p) => p.id === proj.id);
      if (fresh) setProj(fresh);
      onChanged && onChanged();
    } catch { /* noop */ }
  };

  const setMode = async (newMode) => {
    setModeBusy(true);
    try {
      await api.patch(`/admin/projects/${proj.id}/payment-mode`, { payment_mode: newMode });
      toast.success(newMode === "auto_card" ? "Prélèvement automatique activé 💳" : "Mode lien manuel activé 📧");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setModeBusy(false); }
  };

  const charge = async (idx) => {
    if (!window.confirm(`Prélever maintenant l'échéance #${idx} sur la carte enregistrée ?`)) return;
    setBusy(idx);
    try {
      const r = await api.post(`/admin/projects/${proj.id}/installments/${idx}/charge`);
      if (r.data.status === "paid") toast.success(`Échéance #${idx} prélevée avec succès ✓`);
      else if (r.data.status === "failed") toast.error(`Échec : ${r.data.error || "carte refusée"} — un email de relance a été envoyé`);
      else toast.success("Action effectuée");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const sendLink = async (idx) => {
    setBusy(idx);
    try {
      const r = await api.post(`/admin/projects/${proj.id}/installments/${idx}/send-link`);
      toast.success(r.data?.sent_to ? `Lien envoyé à ${r.data.sent_to} ✉️` : "Email envoyé");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const markPaid = async (idx) => {
    if (!window.confirm(`Marquer l'échéance #${idx} comme payée (manuellement) ?`)) return;
    setBusy(idx);
    try {
      await api.patch(`/admin/projects/${proj.id}/installments/${idx}`, { status: "paid", paid_method: "virement" });
      toast.success("Échéance marquée payée");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const installments = proj.installments || [];
  const totalTtc = parseFloat(proj.total_ttc || 0);
  const totalPaid = parseFloat(proj.total_paid || 0);
  const remaining = parseFloat(proj.remaining || 0);

  return (
    <div className="border-t border-black/8 pt-4" data-testid="proj-installments-panel">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CreditCard size={16} className="text-[#06B6D4]" />
        <Label className="font-extrabold text-[#0A1128]">Échéances & facturation automatique</Label>
        <span className="ml-auto text-xs text-[#64748B]">
          {totalPaid.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € payés sur{" "}
          {totalTtc.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € · reste{" "}
          <span className="font-bold text-[#0A1128]">{remaining.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span>
        </span>
      </div>

      {/* Mode selector */}
      <div className="bg-[#FAFAFA] border border-black/8 rounded-xl p-3 mb-3" data-testid="proj-payment-mode-block">
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-[#0A1128]">Mode de paiement</Label>
          <button
            onClick={() => setMode("manual_link")}
            disabled={modeBusy || mode === "manual_link"}
            data-testid="proj-set-manual-link"
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${mode === "manual_link" ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-[#0A1128] border-black/15 hover:bg-[#FAFAFA]"}`}
          >
            <Mail size={12} className="inline mr-1" /> Lien email manuel
          </button>
          <button
            onClick={() => setMode("auto_card")}
            disabled={modeBusy || mode === "auto_card" || !hasCard}
            title={!hasCard ? "Aucune carte enregistrée — le client doit régler un 1er paiement avec sauvegarde de carte" : undefined}
            data-testid="proj-set-auto-card"
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors disabled:opacity-50 ${mode === "auto_card" ? "bg-[#06B6D4] text-[#0A1128] border-[#06B6D4]" : "bg-white text-[#0A1128] border-black/15 hover:bg-[#FAFAFA]"}`}
          >
            <CreditCard size={12} className="inline mr-1" /> Prélèvement auto (carte enregistrée)
          </button>
          {hasCard ? (
            <span className="text-[10px] px-2 py-1 rounded bg-[#A7F3D0]/40 text-[#065F46] font-bold inline-flex items-center gap-1">
              <Check size={10} /> Carte client enregistrée
            </span>
          ) : (
            <span className="text-[10px] px-2 py-1 rounded bg-[#FFDCA8]/30 text-[#92400E] font-bold inline-flex items-center gap-1">
              <AlertTriangle size={10} /> Aucune carte enregistrée
            </span>
          )}
        </div>
        <p className="text-[10px] text-[#64748B] mt-2 leading-relaxed">
          {mode === "auto_card"
            ? "Les échéances futures seront prélevées automatiquement à leur date d'échéance. En cas d'échec, le client recevra un lien de paiement manuel."
            : "Une facture + un lien Stripe seront envoyés au client par email à chaque date d'échéance."}
        </p>
      </div>

      <div className="space-y-2" data-testid="proj-installments-list">
        {installments.map((it) => {
          const pill = STATUS_PILL[it.status] || STATUS_PILL.pending;
          const today = new Date().toISOString().slice(0, 10);
          const isDue = it.due_at && it.due_at <= today && it.status !== "paid";
          const isFirst = (it.index || 0) <= 1;
          return (
            <div
              key={it.index}
              data-testid={`proj-installment-${it.index}`}
              className={`rounded-xl border ${isDue && it.status !== "paid" ? "border-[#FFDCA8] bg-[#FFDCA8]/10" : "border-black/8 bg-white"} p-3 flex items-center gap-3 flex-wrap`}
            >
              <div className="flex items-center gap-2 min-w-[180px]">
                <div className="w-8 h-8 rounded-lg bg-[#0A1128]/8 text-[#0A1128] flex items-center justify-center font-extrabold text-sm">{it.index}</div>
                <div>
                  <div className="font-bold text-sm text-[#0A1128]">{it.label}</div>
                  <div className="text-[10px] text-[#64748B]">Échéance : {it.due_at || "—"}</div>
                </div>
              </div>
              <div className="text-sm font-bold text-[#0A1128]">{(parseFloat(it.amount) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${pill.bg}`}>{pill.label}</span>
              {it.last_error && it.status === "failed" && (
                <span className="text-[10px] text-red-700 max-w-[260px] truncate" title={it.last_error}>{it.last_error}</span>
              )}
              <div className="ml-auto flex items-center gap-1.5">
                {it.status !== "paid" && !isFirst && (
                  <>
                    {hasCard && (
                      <button
                        onClick={() => charge(it.index)}
                        disabled={busy === it.index}
                        data-testid={`proj-installment-charge-${it.index}`}
                        className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] disabled:opacity-50 inline-flex items-center gap-1"
                        title="Prélever maintenant sur la carte enregistrée"
                      >
                        {busy === it.index ? <Loader2 size={10} className="animate-spin" /> : <CreditCard size={10} />}
                        Prélever
                      </button>
                    )}
                    <button
                      onClick={() => sendLink(it.index)}
                      disabled={busy === it.index}
                      data-testid={`proj-installment-send-link-${it.index}`}
                      className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white border border-black/15 text-[#0A1128] hover:bg-[#FAFAFA] disabled:opacity-50 inline-flex items-center gap-1"
                      title="Envoyer le lien Stripe par email"
                    >
                      <Mail size={10} /> Envoyer le lien
                    </button>
                    <button
                      onClick={() => markPaid(it.index)}
                      disabled={busy === it.index}
                      data-testid={`proj-installment-mark-paid-${it.index}`}
                      className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white border border-black/15 text-[#0A1128] hover:bg-[#FAFAFA] disabled:opacity-50 inline-flex items-center gap-1"
                      title="Marquer comme payée (virement / chèque…)"
                    >
                      <Check size={10} /> Marquer payée
                    </button>
                  </>
                )}
                {it.status === "paid" && it.paid_at && (
                  <span className="text-[10px] text-[#065F46] font-bold">payée le {it.paid_at}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[10px] text-[#64748B]">
          Le cron de facturation tourne automatiquement et bille toutes les échéances échues. Vous pouvez aussi le déclencher manuellement.
        </p>
        <button
          onClick={async () => {
            try {
              const r = await api.post("/admin/installments/run-cron");
              toast.success(`Cron exécuté : ${r.data.processed || 0} échéance(s) traitée(s)`);
              await refresh();
            } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
          }}
          data-testid="proj-installments-run-cron"
          className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#0A1128] text-white hover:bg-[#14213d] inline-flex items-center gap-1"
        >
          <RotateCw size={10} /> Lancer le cron de facturation
        </button>
      </div>
    </div>
  );
}
